import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  EyeIcon, 
  EyeSlashIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const EmployeeSignup = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    termsAccepted: false
  });

  // Get token from either path parameter (new) or query parameter (legacy)
  const token = params.token || searchParams.get('token');

  useEffect(() => {
    console.log('[EmployeeSignup] Component mounted');
    console.log('[EmployeeSignup] Full URL:', window.location.href);
    console.log('[EmployeeSignup] Path params:', params);
    console.log('[EmployeeSignup] Search params:', Array.from(searchParams.entries()));
    console.log('[EmployeeSignup] Token from path:', params.token);
    console.log('[EmployeeSignup] Token from query:', searchParams.get('token'));
    console.log('[EmployeeSignup] Final token:', token);
    console.log('[EmployeeSignup] Token type:', typeof token);
    console.log('[EmployeeSignup] Token length:', token ? token.length : 0);
    console.log('[EmployeeSignup] Token falsy?', !token);
    
    if (!token || token.trim() === '') {
      console.log('[EmployeeSignup] No token found in URL parameters');
      setError('Invalid or missing setup token. Please contact your manager for a new invitation.');
      return;
    }
    
    console.log('[EmployeeSignup] Token found, attempting to decode');
    
    // Decode token to get user email and pre-populate form
    try {
      console.log('[EmployeeSignup] Decoding base64 token:', token);
      // The token is base64-encoded JSON, so we decode it using atob
      const decodedToken = atob(token);
      console.log('[EmployeeSignup] Decoded token string:', decodedToken);
      
      const tokenData = JSON.parse(decodedToken);
      console.log('[EmployeeSignup] Parsed token data:', tokenData);
      
      // Validate token structure
      if (!tokenData.userId || !tokenData.type || tokenData.type !== 'account_setup') {
        console.error('[EmployeeSignup] Invalid token structure:', tokenData);
        setError('Invalid setup token format. Please contact your manager for a new invitation.');
        return;
      }
      
      // Check token expiration (24 hours)
      const tokenAge = Date.now() - tokenData.timestamp;
      const twentyFourHours = 24 * 60 * 60 * 1000;
      if (tokenAge > twentyFourHours) {
        console.error('[EmployeeSignup] Token expired. Age:', tokenAge, 'Limit:', twentyFourHours);
        setError('Setup link has expired. Please contact your manager for a new invitation.');
        return;
      }
      
      // We'll fetch the user details to pre-populate the form
      fetchUserDetails(tokenData.userId);
      
      // Clear any existing error since we have a valid token
      setError('');
    } catch (err) {
      console.error('[EmployeeSignup] Error decoding token:', err);
      console.error('[EmployeeSignup] Token that failed:', token);
      setError('Invalid setup token. Please contact your manager for a new invitation.');
    }
  }, [token, params, searchParams]);

  const fetchUserDetails = async (userId) => {
    try {
      console.log('[EmployeeSignup] Fetching user details for:', userId);
      // We can't call the protected API without auth, so we'll let the backend handle
      // the user lookup during form submission. For now, just clear error.
    } catch (err) {
      console.error('[EmployeeSignup] Error fetching user details:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.termsAccepted) {
      setError('You must accept the Terms of Service, Privacy Policy, and User Agreement to continue.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/users/complete-signup', {
        ...formData,
        token
      });

      setSuccess(response.data.message);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Account setup completed! Please log in with your email address.',
            username: formData.email 
          }
        });
      }, 3000);

    } catch (err) {
      console.error('Signup error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to complete account setup';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Invalid Setup Link</h2>
            <p className="mt-2 text-sm text-gray-600">
              This setup link is invalid or has expired. Please contact your manager for a new invitation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <CheckIcon className="mx-auto h-12 w-12 text-green-500" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Setup Complete!</h2>
            <p className="mt-2 text-sm text-gray-600">{success}</p>
            <p className="mt-4 text-sm text-gray-500">Redirecting you to login...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Complete Your Account Setup
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Welcome! Please complete your account setup to get started.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                value={formData.firstName}
                onChange={handleInputChange}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="First name"
              />
            </div>
            
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                Last Name
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                value={formData.lastName}
                onChange={handleInputChange}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Last name"
              />
            </div>
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              placeholder="(555) 123-4567"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              placeholder="your.email@company.com"
            />
            <p className="mt-1 text-xs text-gray-500">
              You'll use this email address to log into your account
            </p>
          </div>


          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="mt-1 relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                minLength={6}
                value={formData.password}
                onChange={handleInputChange}
                className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Create a secure password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Must be at least 6 characters long
            </p>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="termsAccepted"
                name="termsAccepted"
                type="checkbox"
                required
                checked={formData.termsAccepted}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="termsAccepted" className="font-medium text-gray-700">
                By continuing, you agree to our{' '}
                <a href="/terms" className="text-blue-600 hover:text-blue-500" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>
                ,{' '}
                <a href="/privacy" className="text-blue-600 hover:text-blue-500" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                , and{' '}
                <a href="/user-agreement" className="text-blue-600 hover:text-blue-500" target="_blank" rel="noopener noreferrer">
                  User Agreement
                </a>
              </label>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || !formData.termsAccepted}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${
                loading || !formData.termsAccepted
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Setting up your account...
                </div>
              ) : (
                'Complete Account Setup'
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              Need help? Contact your manager or system administrator.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeSignup;